import { lazy, Suspense, type ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import RequirePermission from '@/auth/RequirePermission';
import { Perm } from '@/auth/permissions';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';

// The driver app is its own chunk: its pages load only when someone opens /drive.
const DriveLayout = lazy(() => import('./DriveLayout'));
const DriveSignInPage = lazy(() => import('./pages/DriveSignInPage'));
const TripAuthorityVerifyPage = lazy(() => import('./pages/TripAuthorityVerifyPage'));
const DriveTodayPage = lazy(() => import('./pages/DrivePages').then((m) => ({ default: m.DriveTodayPage })));
const DriveTripPage = lazy(() => import('./pages/DrivePages').then((m) => ({ default: m.DriveTripPage })));
const DriveFuelPage = lazy(() => import('./pages/DrivePages').then((m) => ({ default: m.DriveFuelPage })));
const DriveCheckPage = lazy(() => import('./pages/DrivePages').then((m) => ({ default: m.DriveCheckPage })));
const DriveMorePage = lazy(() => import('./pages/DrivePages').then((m) => ({ default: m.DriveMorePage })));
const DriveWorkshopPage = lazy(() => import('./pages/DriveWorkPages').then((m) => ({ default: m.DriveWorkshopPage })));
const DriveApprovalsPage = lazy(() => import('./pages/DriveWorkPages').then((m) => ({ default: m.DriveApprovalsPage })));

const wait = (element: ReactNode) => (
  <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>{element}</Suspense>
);

/** The driver app (docs §175): signed in, in its own mobile frame, for the Fleet module and the Drive right. */
export const driveRoutes: RouteObject[] = [
  {
    path: 'drive',
    element: wait(
      <RequirePermission anyOf={[Perm.Fleet.Drive, Perm.Fleet.Workshop, Perm.Fleet.Approve]}>
        <RequireModule module={Modules.Fleet}><DriveLayout /></RequireModule>
      </RequirePermission>,
    ),
    children: [
      { index: true, element: wait(<DriveTodayPage />) },
      { path: 'trip/:id?', element: wait(<DriveTripPage />) },
      { path: 'fuel', element: wait(<DriveFuelPage />) },
      { path: 'check', element: wait(<DriveCheckPage />) },
      { path: 'more', element: wait(<DriveMorePage />) },
      { path: 'workshop', element: wait(<RequirePermission permission={Perm.Fleet.Workshop}><DriveWorkshopPage /></RequirePermission>) },
      { path: 'approvals', element: wait(<RequirePermission permission={Perm.Fleet.Approve}><DriveApprovalsPage /></RequirePermission>) },
    ],
  },
];

/** The driver's own sign-in: organisation, driver number, PIN. */
export const drivePublicRoutes: RouteObject[] = [
  { path: '/drive/sign-in', element: wait(<DriveSignInPage />) },
  // The public check behind a trip authority's code (docs §182): anonymous, opened by the token.
  { path: '/verify/trip/:token', element: wait(<TripAuthorityVerifyPage />) },
];
